import { useState } from "react"
import { Button } from "./ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import { RadioGroup, RadioGroupItem } from "./ui/radio-group"
import { Separator } from "./ui/separator"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Calendar } from "./ui/calendar"
import { format, isSameDay,isToday, isAfter } from "date-fns"
// import "../../src/components/globals.css";
// import { cn } from "../lib/utils"
import {
  User,
  Stethoscope,
  UserCheck,
  Trash2,
  CreditCard,
  Utensils,
  Building,
  Star,
  Send,
  CheckCircle2,
  Shield,
  Wrench,
  MessageCircle
} from "lucide-react"
// Import the logo
import PranaamLogo from "../assets/images/pranaamlogo.png";

function RatingQuestion({ question, field, value, onChange }) {
  return (
    <div className="space-y-3">
      <Label className="text-base">{question}</Label>
      <div className="overflow-x-auto pb-2">
        <RadioGroup
          value={value}
          onValueChange={(value) => onChange(field, value)}
          className="flex items-center gap-2 min-w-max"
        >
          {[1, 2, 3, 4, 5].map((rating) => (
            <div key={rating} className="flex flex-col items-center">
              <RadioGroupItem value={rating.toString()} id={`${field}-${rating}`} className="peer sr-only" />
              <Label
                htmlFor={`${field}-${rating}`}
                className="flex h-10 w-10 cursor-pointer flex-col items-center justify-center rounded-full border-2 border-gray-300 bg-white p-2 hover:bg-gray-100 hover:text-gray-900 peer-checked:border-red-900 peer-checked:bg-red-50 peer-checked:text-red-900"
              >
                {rating}
              </Label>
              <span className="mt-1 text-xs text-muted-foreground whitespace-nowrap">
                {rating === 1
                  ? "Very Poor"
                  : rating === 2
                    ? "Poor"
                    : rating === 3
                      ? "Average"
                      : rating === 4
                        ? "Good"
                        : "Excellent"}
              </span>
            </div>
          ))}
          <div className="flex flex-col items-center">
            <RadioGroupItem value="na" id={`${field}-na`} className="peer sr-only" />
            <Label
              htmlFor={`${field}-na`}
              className="flex h-10 w-10 cursor-pointer flex-col items-center justify-center rounded-full border-2 border-gray-300 bg-white p-2 hover:bg-gray-100 hover:text-gray-900 peer-checked:border-red-900 peer-checked:bg-red-50 peer-checked:text-red-900"
            >
              N/A
            </Label>
            <span className="mt-1 text-xs text-muted-foreground whitespace-nowrap">N/A</span>
          </div>
        </RadioGroup>
      </div>
    </div>
  )
}

export default function FeedbackForm() {
  const [submitted, setSubmitted] = useState(false)
  const today = new Date();
  const [formData, setFormData] = useState({
    // Patient Details
    patientName: "",
    patientId: "",
    room_no: "",
    contactNumber: "",
    admissionDate: "", // format: YYYY-MM-DD
    dischargeDate: today,

    // Section 1: Treatment & Medical Care
    treatment_medical_care: "",
    treatment_comments: "",

    // Section 2: Nursing Services
    nursing_services: "",
    nursing_comments: "",

    // Section 3: Housekeeping & Cleanliness
    housekeeping_cleanliness: "",
    housekeeping_comments: "",

    // Section 4: Billing & Discharge Process
    billing_discharge_process: "",
    billing_comments: "",

    // Section 5: Food & Diet Services
    food_diet_services: "",
    food_diet_comments: "",

    // Section 6: Front Office & Support Services
    front_officer: "",
    front_officer_comments: "",

    // Section 7: Security & Parking Facilities
    security_parking: "",
    security_comments: "",
    maintanance: "",
    maintanance_comments: "",
    // Section 8: Overall Experience
    overallExperience: "",
    recommendHospital: "",
    staffAppreciation: "",
    suggestionsForImprovement: ""
  });
  // const handleInputChange = (field, value) => {
  //   if (field === "contactNumber") {
  //     value = value.replace(/\D/g, ""); // Only allow digits
  //   }
  //   setFormData((prev) => ({
  //     ...prev,
  //     [field]: value,
  //   }));
  // };
  const handleInputChange = (field, value) => {
    // clean phone number
    if (field === "contactNumber") {
      value = value.replace(/\D/g, "");
    }

    // custom logic for Admission Date → may reset Discharge
    if (field === "admissionDate") {
      const selected = value ? new Date(value) : null;
      const keepCurrentDischarge =
        selected && isSameDay(selected, today) ? formData.dischargeDate : null;

      setFormData((prev) => ({
        ...prev,
        admissionDate: selected,
        dischargeDate: keepCurrentDischarge, // null if not today
      }));
      return;
    }

    // every other field (incl. dischargeDate)
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  
  const handleRadioChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }
  // const [error, setError] = useState('');
  const [error, setError] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formatDate = (date) => {
      if (!date) return null;
      const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      return offsetDate.toISOString().split("T")[0]; // Returns "YYYY-MM-DD"
    };
    const nameRegex = /^[A-Za-z\s]+$/;
    const errors = {};
    // if (
    //   formData.contactNumber.trim() !== "" &&
    //   !/^[6|7|8|9]\d{9}$/.test(formData.contactNumber.trim())
    // ) {
    //   errors.contactNumber = "Contact Number must be 10 digits and start with 9, 6, 7 or 8.";
    // }
    if (formData.contactNumber.trim() !== "") {
      const number = formData.contactNumber.trim();
    
      if (!/^\d+$/.test(number)) {
        errors.contactNumber = "Contact Number must contain only digits.";
      } else if (number.length !== 10) {
        errors.contactNumber = "Contact Number must be exactly 10 digits.";
      } else if (!/^[6-9]/.test(number)) {
        errors.contactNumber = "Contact Number must start with 6, 7, 8, or 9.";
      }
    }
    
    if (!formData.patientName.trim()) {
      errors.patientName = 'Patient Name is required';
    } else if (!nameRegex.test(formData.patientName.trim())) {
      errors.patientName = 'Patient Name can only contain letters';
    }

    setError(errors);

    if (Object.keys(errors).length > 0) return;

    const payload = {
      patient_name: formData.patientName,
      patient_id: formData.patientId,
      room_no: formData.room_no,
      contact_no: formData.contactNumber,
      date_of_admision: formatDate(formData.admissionDate),
      date_of_discharge: formatDate(formData.dischargeDate),

      // Section 1: Treatment & Medical Care
      treatment_medical_care: formData.treatment_medical_care,
      treatment_comments: formData.treatment_comments,

      // Section 2: Nursing Services
      nursing_services: formData.nursing_services,
      nursing_comments: formData.nursing_comments,

      // Section 3: Housekeeping & Cleanliness
      housekeeping_cleanliness: formData.housekeeping_cleanliness,
      housekeeping_comments: formData.housekeeping_comments,


      // Section 4: Billing & Discharge Process
      billing_discharge_process: formData.billing_discharge_process,
      billing_comments: formData.billing_comments,

      // Section 5: Food & Diet Services
      food_diet_services: formData.food_diet_services,
      food_diet_comments: formData.food_diet_comments,

      // Section 6: Front Office & Support Services
      front_officer: formData.front_officer,
      front_officer_comments: formData.front_officer_comments,

      // Section 7: Security & Parking Facilities
      security_parking: formData.security_parking,
      security_comments: formData.security_comments,

      // Section 8: Overall Experience
      maintanance: formData.maintanance,
      maintanance_comments: formData.maintanance_comments,
      overall_experience: formData.overallExperience,
      recommend_hospital: formData.recommendHospital,
      staff_appreciate: formData.staffAppreciation,
      suggestions_improvement: formData.suggestionsForImprovement,
    };

    try {
      const response = await fetch("https://feedback.pranaamhospitals.com/index.php/Feedback/inpatient_feedback", {

        method: "POST",
        headers: {
          // "Content-Type": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log(result.message);
      setFormData({
        patientName: "",
        patientId: "",
        room_no: "",
        contactNumber: "",
        admissionDate: "",
        dischargeDate: "",
        treatment_medical_care: "",
        treatment_comments: "",
        nursing_services: "",
        nursing_comments: "",
        housekeeping_cleanliness: "",
        housekeeping_comments: "",
        billing_discharge_process: "",
        billing_comments: "",
        food_diet_services: "",
        food_diet_comments: "",
        front_officer: "",
        front_officer_comments: "",
        security_parking: "",
        security_comments: "",
        maintanance: "",
        maintanance_comments: "",
        overallExperience: "",
        recommendHospital: "",
        staffAppreciation: "",
        suggestionsForImprovement: ""
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
    }

  };


  if (submitted) {
    return (
      <div className="container mx-auto py-10 px-4">
        <Card className="w-full max-w-4xl mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <CheckCircle2 className="h-10 w-10 text-red-800" />
            </div>
            <CardTitle className="text-2xl font-bold text-red-800">Thank You for Your Feedback!</CardTitle>
            <CardDescription className="text-lg">
              Your feedback is valuable to us and will help improve our services.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">
              We appreciate you taking the time to share your experience at Pranaam Hospitals. Your insights will be
              used to enhance the quality of care for all our patients.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button
              onClick={() => {
                setSubmitted(false)
              }}
              variant="outline"
              className="border-red-800 text-red-800 hover:bg-red-50"
            >
              Submit Another Response
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <main style={{ minHeight: '100vh', backgroundColor: 'rgb(229 231 235 / 1)' }}>
      <div className="container mx-auto py-10 px-4">
        <Card className="w-full max-w-4xl mx-auto bg-gray-100 shadow-lg">
          <CardHeader className="text-center border-b pb-6 bg-white rounded-t-lg">
            <div className="flex justify-center mb-2">
              <img src={PranaamLogo || "/placeholder.svg"} alt="Pranaam Hospitals Logo" className="h-12 w-auto" />
            </div>
            <CardTitle className="text-xxl font-bold text-red-900">Inpatient Feedback Form</CardTitle>
            <CardDescription className="text-base text-gray-600">
              Thank you for choosing Pranaam Hospitals. We value your feedback to continuously improve our services.
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            <form className="space-y-8">
              {/* Section: Patient Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Patient Details</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="patientName">Patient Name</Label>
                    <Input
                      id="patientName"
                      value={formData.patientName}
                      onChange={(e) => handleInputChange("patientName", e.target.value)}
                      placeholder="Enter full name"
                      className="border-gray-300 focus:border-red-800 focus:ring-red-800"
                      style={{ borderColor: error.patientName ? 'red' : '' }}
                    />
                    {error.patientName && (
                      <p className="text-red-500 text-sm mt-1">{error.patientName}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="patientId">Patient ID / IP Number</Label>
                    <Input
                      id="patientId"
                      value={formData.patientId}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          handleInputChange("patientId", e.target.value);
                        }
                      }}
                      // onChange={(e) => handleInputChange("patientId", e.target.value)}
                      placeholder="Enter ID number"
                      className="border-gray-300 focus:border-red-800 focus:ring-red-800"
                    // type="number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="room_no">Room / Ward No</Label>
                    <Input
                      id="room_no"
                      value={formData.room_no}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          handleInputChange("room_no", e.target.value);
                        }
                      }}
                      // onChange={(e) => handleInputChange("room_no", e.target.value)}
                      placeholder="Enter room number"
                      className="border-gray-300 focus:border-red-800 focus:ring-red-800"
                    // type="number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactNumber">Contact Number (Optional)</Label>
                    <Input
                      id="contactNumber"
                      value={formData.contactNumber}
                      onChange={(e) => handleInputChange("contactNumber", e.target.value)}
                      placeholder="Enter contact number"
                      type="tel"
                      maxLength={10}
                      inputMode="numeric"
                      className="border-gray-300 focus:border-red-800 focus:ring-red-800"
                      style={{ borderColor: error.contactNumber ? 'red' : '' }}
                    />
                    {error.contactNumber && (
                      <p className="text-red-500 text-sm mt-1">{error.contactNumber}</p>
                    )}
                  </div>
                </div>
                {/* <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Date of Admission</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full justify-start text-left font-normal border-gray-300 focus:border-red-800 focus:ring-red-800 ${!formData.admissionDate ? "text-muted-foreground" : ""
                            }`}
                        >
                          {formData.admissionDate
                            ? format(formData.admissionDate, "PPP")
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.admissionDate}
                          onSelect={(date) => handleInputChange("admissionDate", date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dischargeDate">Date of Discharge</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full justify-start text-left font-normal border-gray-300 focus:border-red-800 focus:ring-red-800 ${!formData.dischargeDate || isAfter(formData.dischargeDate, new Date())
                              ? "text-muted-foreground"
                              : ""
                            }`}
                        >
                          {formData.dischargeDate && isToday(formData.dischargeDate)
                            ? format(formData.dischargeDate, "PPP")
                            : formData.dischargeDate && isAfter(formData.dischargeDate, new Date())
                              ? "Select date"
                              : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.dischargeDate}
                          onSelect={(date) => {
                            if (date) {
                              handleInputChange("dischargeDate", date);
                            }
                          }}
                          initialFocus
                          disabled={(date) => {
                            if (!formData.admissionDate) return false;
                            const admission = new Date(formData.admissionDate);
                            admission.setHours(0, 0, 0, 0);
                            return date < admission;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div> */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Date of Admission</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full justify-start text-left font-normal border-gray-300 focus:border-red-800 focus:ring-red-800 ${!formData.admissionDate ? "text-muted-foreground" : ""
                            }`}
                        >
                          {formData.admissionDate
                            ? format(formData.admissionDate, "PPP")
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>

                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.admissionDate || undefined}
                          onSelect={(date) => handleInputChange("admissionDate", date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dischargeDate">Date of Discharge</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={`w-full justify-start text-left font-normal border-gray-300 focus:border-red-800 focus:ring-red-800 ${!formData.dischargeDate ? "text-muted-foreground" : ""
                            }`}
                        >
                          {formData.dischargeDate
                            ? format(formData.dischargeDate, "PPP") // always show the picked date
                            : "Select date"}
                        </Button>
                      </PopoverTrigger>

                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.dischargeDate || undefined}
                          onSelect={(date) => handleInputChange("dischargeDate", date)}
                          initialFocus
                          disabled={(date) => {
                            if (!formData.admissionDate) return false;
                            const admission = new Date(formData.admissionDate);
                            admission.setHours(0, 0, 0, 0);
                            return date < admission; // block dates before admission
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

              </div>

              {/* Section 1: Treatment & Medical Care */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Stethoscope className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Treatment & Medical Care</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="treatment_medical_care"
                    value={formData.treatment_medical_care}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="treatment_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="treatment_comments"
                    value={formData.treatment_comments}
                    onChange={(e) => handleInputChange("treatment_comments", e.target.value)}
                    placeholder="Please mention any comments on treatment medical care"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 2: Nursing Services */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <UserCheck className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Nursing Services</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="nursing_services"
                    value={formData.nursing_services}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="nursing_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="nursing_comments"
                    value={formData.nursing_comments}
                    onChange={(e) => handleInputChange("nursing_comments", e.target.value)}
                    placeholder="Please mention any comments on nursing services"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 3: Housekeeping & Cleanliness */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Trash2 className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Housekeeping & Cleanliness</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="housekeeping_cleanliness"
                    value={formData.housekeeping_cleanliness}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="housekeeping_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="housekeeping_comments"
                    value={formData.housekeeping_comments}
                    onChange={(e) => handleInputChange("housekeeping_comments", e.target.value)}
                    placeholder="Please mention any comments on housekeeping cleanliness"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 4: Billing & Discharge Process */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Billing & Discharge Process</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="billing_discharge_process"
                    value={formData.billing_discharge_process}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="billing_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="billing_comments"
                    value={formData.billing_comments}
                    onChange={(e) => handleInputChange("billing_comments", e.target.value)}
                    placeholder="Please mention any comments on billing discharge process"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 5: Food & Diet Services */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Utensils className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Food & Diet Services</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="food_diet_services"
                    value={formData.food_diet_services}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="food_diet_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="food_diet_comments"
                    value={formData.food_diet_comments}
                    onChange={(e) => handleInputChange("food_diet_comments", e.target.value)}
                    placeholder="Please mention any comments on food diet services"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 6: Front Office & Support Services */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Building className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Front Office & Support Services</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="front_officer"
                    value={formData.front_officer}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="front_officer_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="front_officer_comments"
                    value={formData.front_officer_comments}
                    onChange={(e) => handleInputChange("front_officer_comments", e.target.value)}
                    placeholder="Please mention any comments on front officer support services"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 7: Security & Parking Facilities */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Security & Parking Facilities</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="security_parking"
                    value={formData.security_parking}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="security_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="security_comments"
                    value={formData.security_comments}
                    onChange={(e) => handleInputChange("security_comments", e.target.value)}
                    placeholder="Please mention any comments on security parking facilities"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Wrench className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Maintanance</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="maintanance"
                    value={formData.maintanance}
                    onChange={handleRadioChange}
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="maintanance_comments" className="text-base flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-gray-600" />
                    Comments
                  </Label>
                  <Textarea
                    id="maintanance_comments"
                    value={formData.maintanance_comments}
                    onChange={(e) => handleInputChange("maintanance_comments", e.target.value)}
                    placeholder="Please mention any comments on maintanance"
                    className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                  />
                </div>
              </div>

              {/* Section 8: Overall Experience */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-red-900" />
                  <h2 className="text-xl font-semibold text-red-900">Overall Experience</h2>
                </div>
                <Separator className="bg-red-300" />

                <div className="space-y-6">
                  <RatingQuestion
                    field="overallExperience"
                    value={formData.overallExperience}
                    onChange={handleRadioChange}
                  />

                  <div className="space-y-3">
                    <Label className="text-base">Would you recommend Pranaam Hospitals to others?</Label>
                    <RadioGroup
                      value={formData.recommendHospital}
                      onValueChange={(value) => handleRadioChange("recommendHospital", value)}
                      className="flex items-center gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yes"
                          id="recommend-yes"
                          className="text-red-900 border-red-900 focus:ring-red-900"
                        />
                        <Label htmlFor="recommend-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="no"
                          id="recommend-no"
                          className="text-red-900 border-red-900 focus:ring-red-900"
                        />
                        <Label htmlFor="recommend-no">No</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="na"
                          id="recommend-na"
                          className="text-red-900 border-red-900 focus:ring-red-900"
                        />
                        <Label htmlFor="recommend-na">N/A</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="staffAppreciation" className="text-base">
                      Any staff member you'd like to appreciate?
                    </Label>
                    <Textarea
                      id="staffAppreciation"
                      value={formData.staffAppreciation}
                      onChange={(e) => handleInputChange("staffAppreciation", e.target.value)}
                      placeholder="Please mention name and/or department if possible"
                      className="min-h-[80px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="suggestionsForImprovement" className="text-base">
                      Suggestions for improvement
                    </Label>
                    <Textarea
                      id="suggestionsForImprovement"
                      value={formData.suggestionsForImprovement}
                      onChange={(e) => handleInputChange("suggestionsForImprovement", e.target.value)}
                      placeholder="Please share your suggestions to help us improve"
                      className="min-h-[100px] border-gray-300 focus:border-red-800 focus:ring-red-800"
                    />
                  </div>
                </div>
              </div>
            </form>
          </CardContent>

          <CardFooter className="flex justify-center border-t pt-6">
            <Button
              type="submit"
              onClick={handleSubmit}
              className="flex items-center gap-1 bg-red-900 hover:bg-red-950 px-8 py-2 text-lg text-white"
            >
              Submit Feedback <Send className="h-4 w-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </main>
  )
}
